import { FC, ReactNode, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Atom, atom, useAtomValue, useSetAtom, WritableAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { Wizard } from 'components/wizard';
import {
  FullPageWizardLayout,
  useWizardStep,
  type StepConfig,
} from 'components/full-page-wizard';
import { ProvideRootResource } from 'components/hydrate-atoms/hydrate-atoms';
import { InlineLoading } from '@carbon/react';
import routes from '~/routes';
import {
  upsertCrmTractWizardActionAtom,
  editTractAtom,
  initializeRestoredWorkflowAtom,
  isEntityWithAccountSelectedAtom,
  isTractIntersectionAnalysisCurrentNextEnabled,
  isPreviousEnabledFromCurrentDevelopmentAtom,
  isTractValidAtom,
  tractEnumLoadableAtom,
  wizardNavigateToStepAtom,
} from './atoms';
import { EntitySearchWithAccounts } from './entity-search-with-accounts';
import { NriReview } from './nri-review';
import { TractCreation } from './tract-creation';
import { CurrentDevelopment } from './tract-intersection-analysis/current-development';
import { FutureDevelopment } from './tract-intersection-analysis/future-development';
import { TractRecordType } from './tract-record-type';
import { TractSummary } from './tract-summary';

// ─────────────────────────────────────
// StepValidation — Atom-to-Context Bridge
// ─────────────────────────────────────

interface StepValidationProps {
  isNextEnabledAtom?: Atom<boolean | Promise<boolean>>;
  isBackEnabledAtom?: Atom<boolean | Promise<boolean>>;
  onNextAtom?: WritableAtom<
    unknown,
    [],
    Promise<{ success: boolean; error?: string }>
  >;
  children: ReactNode;
}

/**
 * Bridges existing Jotai validation/action atoms to the FullPageWizard context.
 * Wraps step content — reads atoms and pushes state to setNextEnabled/setBackEnabled/setOnNext.
 * Step components stay untouched.
 * @param {StepValidationProps} props - The component props.
 * @return {JSX.Element} The wrapped children.
 */
const StepValidation: FC<StepValidationProps> = ({
  isNextEnabledAtom: nextAtom,
  isBackEnabledAtom: backAtom,
  onNextAtom: actionAtom,
  children,
}) => {
  const { setNextEnabled, setBackEnabled, setOnNext } = useWizardStep();

  const effectiveNextAtom = useMemo(
    () => (nextAtom ? loadable(nextAtom) : loadable(atom(true))),
    [nextAtom]
  );
  const effectiveBackAtom = useMemo(
    () => (backAtom ? loadable(backAtom) : loadable(atom(true))),
    [backAtom]
  );

  const nextLoadable = useAtomValue(effectiveNextAtom);
  useEffect(() => {
    setNextEnabled(
      nextLoadable.state === 'hasData' ? nextLoadable.data : false
    );
  }, [nextLoadable, setNextEnabled]);

  const backLoadable = useAtomValue(effectiveBackAtom);
  useEffect(() => {
    setBackEnabled(
      backLoadable.state === 'hasData' ? backLoadable.data : false
    );
  }, [backLoadable, setBackEnabled]);

  const noOpActionAtom = useMemo(
    () =>
      atom(null, () =>
        Promise.resolve({ success: true as const, error: undefined })
      ),
    []
  );
  const triggerAction = useSetAtom(actionAtom ?? noOpActionAtom);

  useEffect(() => {
    if (actionAtom) {
      setOnNext(async () => {
        const result = await triggerAction();
        if (!result.success) throw new Error(result.error ?? 'Action failed');
      });
    }
  }, [actionAtom, triggerAction, setOnNext]);

  return <>{children}</>;
};

// ─────────────────────────────────────
// Full-Page Wizard Content
// ─────────────────────────────────────

interface TractWizardContentProps {
  editTract: { tractId: string | null; subtractId: string | null };
}

/**
 * Configures wizard steps and renders FullPageWizardLayout. Loads
 * enums from atoms. Six steps — the legacy NRI Review step is removed
 * because its slot/UAT verification surface was absorbed into Current
 * Development (T8/T9). The legacy `<TractWizardModal>` below preserves
 * the 7-step shape for entity-inventory / CRM-tracts callers that
 * haven't migrated to the route-based wizard yet.
 *
 * @param {TractWizardContentProps} props - The component props.
 * @return {JSX.Element} The wizard layout.
 */
const TractWizardContent: FC<TractWizardContentProps> = ({ editTract }) => {
  useAtomValue(initializeRestoredWorkflowAtom);
  const history = useHistory();

  const tractEnums = useAtomValue(tractEnumLoadableAtom);
  const tractRecordTypes =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.tractRecordType
      : [];
  const accountType =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.accountType
      : [];
  const accountSource =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.accountSource
      : [];

  if (tractEnums.state === 'loading') {
    return <InlineLoading description="Loading..." />;
  }

  const steps: StepConfig[] = [
    {
      label: 'Record Type*',
      content: <TractRecordType tractRecordTypes={tractRecordTypes} />,
    },
    {
      label: 'Entity*',
      content: (
        <StepValidation isNextEnabledAtom={isEntityWithAccountSelectedAtom}>
          <EntitySearchWithAccounts
            accountType={accountType}
            accountSource={accountSource}
          />
        </StepValidation>
      ),
    },
    {
      label: 'Tract*',
      content: (
        <StepValidation
          isNextEnabledAtom={isTractValidAtom}
          onNextAtom={upsertCrmTractWizardActionAtom}
        >
          <TractCreation />
        </StepValidation>
      ),
      nextLabel: 'Save/Next',
    },
    {
      label: 'Current Development',
      content: (
        <StepValidation
          isNextEnabledAtom={isTractIntersectionAnalysisCurrentNextEnabled}
          isBackEnabledAtom={isPreviousEnabledFromCurrentDevelopmentAtom}
        >
          <CurrentDevelopment tractId={editTract.tractId} />
        </StepValidation>
      ),
    },
    {
      label: 'Future Development',
      content: (
        <StepValidation
          isNextEnabledAtom={isTractIntersectionAnalysisCurrentNextEnabled}
          isBackEnabledAtom={isTractIntersectionAnalysisCurrentNextEnabled}
        >
          <FutureDevelopment />
        </StepValidation>
      ),
    },
    {
      label: 'Summary',
      content: <TractSummary />,
      isInitialStep: !!editTract.tractId,
    },
  ];

  return (
    <FullPageWizardLayout
      breadcrumbs={[[routes.crmTractsAndSubtractsPath, 'CRM']]}
      title="CRM Tract Manager"
      steps={steps}
      onCancel={() => history.push(routes.crmTractsAndSubtractsPath)}
    />
  );
};

// ─────────────────────────────────────
// TractWizard — Route-based Entry Point
// ─────────────────────────────────────

/**
 * CRM Tract Wizard page component. Reads tractId from route params
 * to determine create vs edit mode. Wraps content in ProvideRootResource
 * for atom initialization.
 * @return {JSX.Element} The wizard page.
 */
export const TractWizard: FC = () => {
  const { tractId } = useParams<{ tractId?: string }>();

  const editTract = useMemo(
    () => ({
      tractId: tractId ?? null,
      subtractId: null,
    }),
    [tractId]
  );

  return (
    <ProvideRootResource initialValues={[[editTractAtom, editTract]]}>
      <TractWizardContent editTract={editTract} />
    </ProvideRootResource>
  );
};

// ─────────────────────────────────────
// TractWizardModal — Legacy modal wrapper
// ─────────────────────────────────────

interface TractWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  tractRecordTypes: Array<{ value: string; label: string }>;
  accountType: Array<{ value: string; label: string }>;
  accountSource: Array<{ value: string; label: string }>;
  editTract?: { tractId: string | null; subtractId: string | null };
}

/**
 * Legacy modal-based CRM Tract Wizard. Used by entity inventory, CRM tracts
 * table, NRI table, and allocation manager until they migrate to route-based
 * navigation.
 * @param {TractWizardModalProps} props - The component props.
 * @return {JSX.Element | null} The modal wizard or null when closed.
 */
const TractWizardModalContent: FC<{
  isOpen: boolean;
  onClose: () => void;
  tractRecordTypes: Array<{ value: string; label: string }>;
  accountType: Array<{ value: string; label: string }>;
  accountSource: Array<{ value: string; label: string }>;
  editTract: { tractId: string | null; subtractId: string | null };
}> = ({
  isOpen,
  onClose,
  tractRecordTypes,
  accountType,
  accountSource,
  editTract,
}) => {
  useAtomValue(initializeRestoredWorkflowAtom);

  const steps = [
    {
      label: 'Record Type*',
      component: <TractRecordType tractRecordTypes={tractRecordTypes} />,
    },
    {
      label: 'Entity*',
      component: (
        <EntitySearchWithAccounts
          accountType={accountType}
          accountSource={accountSource}
        />
      ),
      isNextEnabledAtom: isEntityWithAccountSelectedAtom,
    },
    {
      label: 'Tract*',
      component: <TractCreation />,
      isNextEnabledAtom: isTractValidAtom,
      onNextAtom: upsertCrmTractWizardActionAtom,
      errorLabel: editTract.tractId
        ? 'Failed to update tract'
        : 'Failed to create tract',
      allowExit: true,
      exitButtonLabel: 'Save/Close',
      nextButtonLabel: 'Save/Next',
    },
    {
      label: 'Current Development',
      component: <CurrentDevelopment tractId={editTract.tractId} />,
      isNextEnabledAtom: isTractIntersectionAnalysisCurrentNextEnabled,
      isPreviousEnabledAtom: isPreviousEnabledFromCurrentDevelopmentAtom,
    },
    {
      label: 'Future Development',
      component: <FutureDevelopment />,
      isNextEnabledAtom: isTractIntersectionAnalysisCurrentNextEnabled,
      isPreviousEnabledAtom: isTractIntersectionAnalysisCurrentNextEnabled,
    },
    { label: 'NRI Review', component: <NriReview /> },
    {
      label: 'Summary',
      component: <TractSummary />,
      isInitialStep: !!editTract.tractId,
    },
  ];

  return (
    <Wizard
      header="Create CRM Tract"
      isOpen={isOpen}
      onRequestClose={onClose}
      steps={steps}
      navigateToStepAtom={wizardNavigateToStepAtom}
    />
  );
};

export const TractWizardModal = ({
  isOpen,
  onClose,
  tractRecordTypes,
  accountType,
  accountSource,
  editTract = { tractId: null, subtractId: null },
}: TractWizardModalProps) => {
  if (!isOpen) return null;

  return (
    <ProvideRootResource initialValues={[[editTractAtom, editTract]]}>
      <TractWizardModalContent
        isOpen={isOpen}
        onClose={onClose}
        tractRecordTypes={tractRecordTypes}
        accountType={accountType}
        accountSource={accountSource}
        editTract={editTract}
      />
    </ProvideRootResource>
  );
};
